import React, { ChangeEvent, useState } from 'react';
import { ControlProps, rankWith, uiTypeIs, and, schemaMatches } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { Button, Typography, Box, FormHelperText } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Tester that triggers if the schema type is string and format is 'binary'
 */
export const FileBase64RendererTester = rankWith(
  5,
  and(
    uiTypeIs('Control'),
    schemaMatches(schema => schema.hasOwnProperty('format') && schema.format === 'binary')
  )
);

/**
 * FileBase64Renderer handles file uploads by converting them to Base64 strings.
 * It supports two data formats based on the JSON Schema type:
 * 1. String: Returns only the base64 data URL.
 * 2. Object: Returns an object containing content (base64), name, size, and type.
 * 
 * @example
 * // 1. Scenario: Expecting just the Base64 string
 * const jsonSchema = {
 *   "type": "string",
 *   "format": "binary"
 * };
 * 
 * // 2. Scenario: Expecting full metadata
 * const jsonSchemaMeta = {
 *   "type": "object",
 *   "format": "binary",
 *   "properties": {
 *     "content": { "type": "string" },
 *     "name": { "type": "string" },
 *     "size": { "type": "number" },
 *     "type": { "type": "string" }
 *   }
 * };
 * 
 * // UI Schema Configuration
 * const uiSchema = {
 *   "type": "Control",
 *   "scope": "#/properties/myFile",
 *   "options": {
 *     "accept": ".pdf,image/*", // HTML accept attribute
 *     "maxFileSize": 1048576    // 1MB in bytes
 *   }
 * };
 */
const FileBase64Renderer = (props: ControlProps) => {
  const { t } = useTranslation();
  const { data, handleChange, path, label, required, description, errors, visible, readonly, uischema, schema } = props;
  const [localError, setLocalError] = useState<string | null>(null);

  if (!visible) return null;

  // Configuration from uischema options or schema
  const accept = uischema.options?.accept || (schema as any).contentMediaType;
  const maxFileSize = uischema.options?.maxFileSize; // Size in bytes

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLocalError(null);

    // Validate file size if maxFileSize is provided
    if (maxFileSize && file.size > maxFileSize) {
      const sizeInMb = (maxFileSize / (1024 * 1024)).toFixed(2);
      setLocalError(t('fileRenderer.error.size', { 
        defaultValue: `File is too large. Maximum allowed size is ${sizeInMb}MB.`,
        size: sizeInMb 
      }));
      e.target.value = ''; // Reset input
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      // reader.result contains the base64 string with the data URL prefix
      const base64String = reader.result as string;
      
      if (schema.type === 'object') {
        handleChange(path, {
          content: base64String,
          name: file.name,
          size: file.size,
          type: file.type
        });
      } else {
        handleChange(path, base64String);
      }
    };
    reader.onerror = () => {
      console.error("File reading failed");
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box sx={{ my: 2, textAlign: 'left' }}>
      <Typography variant="subtitle1" color={errors ? 'error' : 'textPrimary'}>
        {label}{required ? ' *' : ''}
      </Typography>
      
      <Button 
        variant="outlined" 
        component="label" 
        disabled={readonly}
        sx={{ mt: 1 }}
      >
        {data ? t('fileRenderer.change', 'Change File') : t('fileRenderer.upload', 'Upload File')}
        <input type="file" hidden onChange={onFileChange} accept={accept} />
      </Button>

      {data && (
        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
          {typeof data === 'object' && data?.name 
            ? t('fileRenderer.attachedWithMeta', {
                defaultValue: `File attached: ${data.name} (${(data.size / 1024).toFixed(2)} KB)`,
                name: data.name,
                size: (data.size / 1024).toFixed(2)
              })
            : t('fileRenderer.attached', 'File attached (Base64 encoded)')}
        </Typography>
      )}

      {description && <FormHelperText>{description}</FormHelperText>}
      {errors && <FormHelperText error>{errors}</FormHelperText>}
      {localError && <FormHelperText error>{localError}</FormHelperText>}
    </Box>
  );
};

export default withJsonFormsControlProps(FileBase64Renderer);